import { Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { isStaffRole } from '@/types';
import { paths } from './paths';
import type { UUID } from '@/types';

/** `/admin/report` now lives on the report page, tab Aggregati. */
export function RedirectAggregatiReport(): JSX.Element {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'aggregati');
  const query = params.toString();
  return <Navigate to={`${paths.report.root}?${query}${hash}`} replace />;
}

/** Keeps the query string when an old path is renamed. */
export function RedirectPreserveSearch({ to }: { to: string }): JSX.Element {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/** `/admin/tasks/:taskId` → `/admin/attivita/:taskId`. */
export function RedirectAdminTask(): JSX.Element {
  const { taskId } = useParams<{ taskId: string }>();
  if (!taskId) return <Navigate to={paths.admin.activities.list} replace />;
  return <Navigate to={paths.admin.activities.detail(taskId as UUID)} replace />;
}

/**
 * Email SLA and workers still emit `/admin/ticket?id=`.
 * Query strings are not distinct routes in RR v6, so this wraps the coda page.
 */
export function AdminTicketIdRedirect({ children }: { children: JSX.Element }): JSX.Element {
  const [params] = useSearchParams();
  const id = params.get('id')?.trim();
  if (id) return <Navigate to={paths.ticket.detail(id as UUID)} replace />;
  return children;
}

/**
 * The mail for a new cedolino points at `/cedolini/:id` (the client portal).
 * Staff who open that link belong on `/admin/cedolini/:id`; the portal route
 * is only for `cliente_admin`.
 */
export function CedoliniPortalEntry(): JSX.Element {
  const ruolo = useAuthStore((state) => state.user?.ruolo ?? null);
  const { pathname } = useLocation();
  if (isStaffRole(ruolo)) {
    const id = pathname.match(/^\/cedolini\/([0-9a-fA-F-]{36})\/?$/)?.[1];
    return <Navigate to={id ? paths.admin.cedolino(id as UUID) : paths.admin.cedolini} replace />;
  }
  if (ruolo !== 'cliente_admin') return <Navigate to={paths.forbidden} replace />;
  return <Outlet />;
}

/** Staff hitting `/utenti` land on the admin namespace; tenant admins stay. */
export function UtentiEntry({ children }: { children: JSX.Element }): JSX.Element {
  const ruolo = useAuthStore((state) => state.user?.ruolo ?? null);
  if (isStaffRole(ruolo)) return <Navigate to={paths.admin.users} replace />;
  return children;
}
